import { Fragment, useEffect, useRef, type ReactNode } from "react";
import { Check, Maximize2, PawPrint, Pencil } from "lucide-react";
import { Button } from "@fawkes/ui";
import "./WizardModal.css";

export interface WizardModalStep {
  key: string;
  /** Intro heading — the step question ("Hoeveel dieren wil je inboeken?"). */
  title: string;
  /** Optional intro subtitle under the heading. */
  subtitle?: string;
  /** 0-based index into `phases` this step belongs to. */
  phase: number;
  content: ReactNode;
  /** Short recap shown as a completed-step summary card once this step is behind the active one. */
  summary?: ReactNode;
  /** The body renders its own heading — suppress the intro heading (like the old StepWizard h3). */
  hasBodyTitle?: boolean;
  /** The body drives its own navigation — hide the footer action row. */
  hideActions?: boolean;
  /** Whether the step content sits in the standard bordered white content-card (design default).
      Self-carding steps (count/type selection grids, two-column panel screens) set this false so the
      content isn't double-wrapped. Defaults to true. */
  card?: boolean;
}

/**
 * The paw-icon step header (paw-in-circle + title + optional info), shared by both wizards so every
 * step whose body renders its own heading looks the same as the WizardModal intro — instead of the
 * plain bold title those steps used before. Kept here (next to the `.fawkes-om-modal__intro` styles)
 * so Inboeken and Uitboeken import one implementation.
 */
export function StepIntro({ title, info }: { title: ReactNode; info?: ReactNode }) {
  return (
    <div className="fawkes-om-modal__intro fawkes-om-modal__intro--body">
      <span className="fawkes-om-modal__intro-icon">
        <PawPrint size={22} aria-hidden="true" />
      </span>
      <div>
        <h3>{title}</h3>
        {info != null && info !== "" ? <p>{info}</p> : null}
      </div>
    </div>
  );
}

/**
 * WizardModal — the redesigned intake-wizard chrome: a modal card with a header, a horizontal phase
 * stepper, an intro + step body, and a footer action row. Replaces the vertical `StepWizard` for the
 * Inboeken wizard only (additive — the release wizard keeps StepWizard). Ported from the
 * claude.ai/design "Fawkes — In/uitcheck wizard" redesign (`fawkes-om-modal`).
 */
export function WizardModal({
  title,
  subtitle,
  phases,
  steps,
  active,
  furthest,
  onEdit,
  onBack,
  onConfirm,
  onCancel,
  onExpand,
  expandLabel,
  confirmDisabled = false,
  busy = false,
  confirmLabel,
  finishLabel,
  backLabel,
  cancelLabel,
}: {
  title: string;
  /** Small line under the modal title in the header (e.g. "Registreer nieuwe dieren in de opvang"). */
  subtitle?: string;
  /** Phase labels shown in the top stepper (grouping the many steps into a few logical phases). */
  phases: string[];
  steps: WizardModalStep[];
  active: number;
  furthest: number;
  /** Jump to a step (used by the stepper to go back to a reached phase). */
  onEdit: (index: number) => void;
  onBack: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Optional "open as page" affordance in the header. */
  onExpand?: () => void;
  expandLabel?: string;
  confirmDisabled?: boolean;
  busy?: boolean;
  confirmLabel: string;
  finishLabel: string;
  backLabel: string;
  cancelLabel: string;
}) {
  const step = steps[Math.min(active, steps.length - 1)];
  const activePhase = step?.phase ?? 0;
  const furthestPhase = steps[Math.min(furthest, steps.length - 1)]?.phase ?? 0;
  const isLast = active === steps.length - 1;
  const showIntro = Boolean(step?.subtitle) || !step?.hasBodyTitle;

  // Elke stap begint bovenaan. Zonder dit blijft de body staan waar je klikte: wie onderaan een
  // lange dierenlijst doorschuift, kijkt daarna naar het midden van de volgende stap.
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = 0;
  }, [active]);

  // First step index of each phase — lets a completed stepper chip jump back to that phase.
  const firstStepOfPhase = (phaseIdx: number) => steps.findIndex((s) => s.phase === phaseIdx);

  return (
    <div className="fawkes-om-modal fawkes-inboeken">
      <div className="fawkes-om-modal__header">
        <span className="fawkes-om-modal__icon">
          <PawPrint size={20} aria-hidden="true" />
        </span>
        <div className="fawkes-om-modal__titles">
          <h2 className="fawkes-om-modal__title">{title}</h2>
          {subtitle && <p className="fawkes-om-modal__subtitle">{subtitle}</p>}
        </div>
        {onExpand && (
          <button
            type="button"
            className="fawkes-om-modal__close fawkes-om-modal__expand"
            aria-label={expandLabel}
            title={expandLabel}
            onClick={onExpand}
          >
            <Maximize2 size={16} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="fawkes-om-modal__close"
          aria-label={cancelLabel}
          title={cancelLabel}
          onClick={onCancel}
        >
          ✕
        </button>
      </div>

      <div className="fawkes-om-modal__steps">
        {phases.map((label, i) => {
          const state = i === activePhase ? "active" : i < activePhase ? "done" : "";
          const reached = i <= furthestPhase;
          const target = firstStepOfPhase(i);
          const clickable = reached && i !== activePhase && target >= 0;
          const className = "fawkes-om-modal__step" + (state ? ` fawkes-om-modal__step--${state}` : "");
          const inner = (
            <>
              <span className="fawkes-om-modal__step-num">
                {state === "done" ? <Check size={14} strokeWidth={3} aria-hidden="true" /> : i + 1}
              </span>
              <span className="fawkes-om-modal__step-label">{label}</span>
            </>
          );
          return (
            <Fragment key={label}>
              {i > 0 && (
                <span
                  className={"fawkes-om-modal__step-line" + (i <= activePhase ? " fawkes-om-modal__step-line--done" : "")}
                />
              )}
              {/* Only reached, non-active phases are interactive (jump back). Active/future phases are
                  non-interactive text — keeps them out of the button a11y tree so the "Bevestigen"
                  phase never collides with the footer confirm button. */}
              {clickable ? (
                <button type="button" className={className} onClick={() => onEdit(target)}>
                  {inner}
                </button>
              ) : (
                <span className={className} aria-current={i === activePhase ? "step" : undefined}>
                  {inner}
                </span>
              )}
            </Fragment>
          );
        })}
      </div>

      <div className="fawkes-om-modal__body" ref={bodyRef}>
        {steps.slice(0, active).map((s, i) =>
          s.summary == null ? null : (
            <div key={s.key} className="fawkes-om-modal__recap">
              <span className="fawkes-om-modal__recap-text">{s.summary}</span>
              <button
                type="button"
                className="fawkes-om-modal__recap-edit"
                onClick={() => onEdit(i)}
                aria-label={backLabel}
                title={backLabel}
              >
                <Pencil size={15} aria-hidden="true" />
              </button>
            </div>
          ),
        )}
        {showIntro && (
          <div className="fawkes-om-modal__intro">
            <span className="fawkes-om-modal__intro-icon">
              <PawPrint size={22} aria-hidden="true" />
            </span>
            <div>
              <h3>{step?.title}</h3>
              {step?.subtitle && <p>{step.subtitle}</p>}
            </div>
          </div>
        )}
        <div className="fawkes-om-modal__content">
          {step?.card === false ? (
            step?.content
          ) : (
            <div className="fawkes-om-modal__content-card">{step?.content}</div>
          )}
        </div>
      </div>

      {!step?.hideActions && (
        <div className="fawkes-om-modal__footer">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            ✕ {cancelLabel}
          </Button>
          {active > 0 && (
            <Button variant="secondary" onClick={onBack} disabled={busy}>
              ‹ {backLabel}
            </Button>
          )}
          <span className="fawkes-om-modal__footer-spacer" />
          <Button variant="primary" onClick={onConfirm} disabled={confirmDisabled || busy}>
            {isLast ? (
              <>
                {finishLabel} <Check size={15} aria-hidden="true" style={{ verticalAlign: "-2px" }} />
              </>
            ) : (
              <>{confirmLabel} ›</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
